import React, { Component } from 'react';
import Downshift from 'downshift';
import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';
import { defaultDebounceMs } from '../../../constants/inputBehaviours';
import { getDisplayZone } from 'common/types/GetDisplayZone';
import { loadZones, dischargeZones } from '../../../models/Location';
import { getDataset } from '../../../models/Datasets';
import FortDownshiftInput from './FortDownshiftInput';
import {
    KEY_TAB,
    KEY_ENTER,
    KEY_BACKSPACE,
    KEY_DELETE,
    KEY_LEFT,
    KEY_RIGHT,
} from '../../../constants/keyboardCodes';
import {
    DIRECTION_BACKWARD,
    DIRECTION_FORWARD,
} from '../../../models/common/EntityPart';
import { CYPRESS_DATA_ATTRIBUTES } from 'constant';

const { GRID_CELL_INPUT } = CYPRESS_DATA_ATTRIBUTES;

class ZoneInput extends Component {
    constructor(props) {
        super(props);
        this.inputRef = React.createRef();
        this.downshiftRef = React.createRef();
        this.state = this.initialState(props);
        this.stateReducer = this.stateReducer.bind(this);
        this.handleInputKeyDown = this.handleInputKeyDown.bind(this);
        this.handleInputKeyUp = this.handleInputKeyUp.bind(this);
        this.searchZones = debounce(
            this.searchZones.bind(this),
            defaultDebounceMs
        );
        // Guard against spurious onInputCleared during Downshift initialization
        this._userHasInteracted = false;
    }

    UNSAFE_componentWillMount() {
        if (this.state.initialSeparator) {
            this.props.onSeparatorEntered?.(this.props.initialChar);
        } else if (this.state.inputValue && !this.props.initialValue) {
            // Only pre-search when it's a typed char (initialChar), not a
            // pre-seeded existing value (initialValue).
            this.searchZones(this.state.inputValue);
        }
    }

    initialState = (props) => {
        let inputValue = '';
        let initialIsOpen = false;
        let initialSeparator = false;
        if (props.initialChar) {
            if (this.isSeparatorCharacter(props.initialChar)) {
                initialSeparator = true;
            } else {
                inputValue = props.initialChar;
                initialIsOpen = true;
            }
        } else if (props.initialValue) {
            // Pre-seed the input with an existing value without opening the
            // dropdown or triggering a search — user must type to search.
            inputValue = props.initialValue;
        }
        return {
            matchedZones: [],
            inputValue,
            initialIsOpen,
            initialSeparator,
            isDeleting: false,
        };
    };

    isSeparatorCharacter(character) {
        if (this.props.separatorCharacters) {
            return this.props.separatorCharacters.indexOf(character) > -1;
        }
        return false;
    }

    get availableZones() {
        const { field, context } = this.props;
        if (field === 'loadZones' || field === 'loadZone') {
            return loadZones(context.datasetId);
        } else if (
            field === 'dischargeZones' ||
            field === 'dischargeZone' ||
            field === 'deliveryZones' ||
            field === 'deliveryZone'
        ) {
            return dischargeZones(context.datasetId);
        }
        return [];
    }

    searchZones(text) {
        const upper = text.toUpperCase();
        let matches = this.availableZones
            .filter(
                (z) =>
                    getDisplayZone(z, this.props.field)
                        .toUpperCase()
                        .includes(upper) || z.toUpperCase().includes(upper)
            )
            .map((z) => ({
                id: z,
                name: z,
                displayName: getDisplayZone(z, this.props.field),
                type: 'known',
            }))
            .sort((a, b) => {
                const ai = a.displayName.toUpperCase().indexOf(upper);
                const bi = b.displayName.toUpperCase().indexOf(upper);
                return ai - bi || a.displayName.localeCompare(b.displayName);
            });

        const { allowFreeTextZone } = getDataset(this.props.context.datasetId);
        if (
            allowFreeTextZone &&
            !matches.some((z) => z.id.toUpperCase() === upper)
        ) {
            matches.push({
                id: upper,
                name: upper,
                displayName: upper,
                type: 'custom',
            });
        }

        this.setState({ matchedZones: matches });
    }

    itemToString = (zone) => (zone ? zone.name : '');

    reset() {
        this.setState((state, props) => this.initialState(props));
    }

    stateReducer(state, changes) {
        switch (changes.type) {
            case Downshift.stateChangeTypes.changeInput: {
                const value = changes.inputValue;
                const valueToSearch = value.trim();

                this.setState({ inputValue: value });

                if (valueToSearch === '') {
                    this.searchZones.cancel();
                    this.setState({ matchedZones: [] });
                    changes.isOpen = false;
                    // Only fire onInputCleared after deliberate user interaction,
                    // not during Downshift's initialization reconciliation.
                    if (this._userHasInteracted) {
                        this.props.onInputCleared?.();
                    }
                } else if (this.isSeparatorCharacter(valueToSearch)) {
                    this.setState({ matchedZones: [] });
                    changes.isOpen = false;
                    changes.inputValue = '';
                    this.props.onSeparatorEntered?.(valueToSearch);
                    this.reset();
                } else {
                    this.searchZones(value);
                    changes.isOpen = true;
                }
                return { ...changes };
            }
            case Downshift.stateChangeTypes.keyDownEnter:
            case Downshift.stateChangeTypes.clickItem: {
                if (changes.selectedItem) {
                    // Don't pass a keyCode — lets MultipleZoneSelect add the
                    // zone without closing the editor, matching CargoInput behaviour.
                    this.props.onZoneSelected(changes.selectedItem);
                }
                this.reset();
                changes.inputValue = '';
                changes.isOpen = false;
                return { ...changes };
            }
            case Downshift.stateChangeTypes.blurInput: {
                changes.inputValue = '';
                changes.isOpen = false;
                return { ...changes };
            }
            default:
                return changes;
        }
    }

    handleInputKeyDown(e) {
        this._userHasInteracted = true;
        switch (e.keyCode) {
            case KEY_BACKSPACE: {
                const { isDeleting, inputValue } = this.state;
                if (!isDeleting && !inputValue) {
                    if (this.props.onDelete) {
                        this.props.onDelete(DIRECTION_BACKWARD);
                    } else {
                        this.props.onInputCleared?.();
                    }
                }
                this.setState({ isDeleting: true });
                break;
            }
            case KEY_TAB: {
                e.preventDefault();
                e.stopPropagation();
                const items = this.downshiftRef.current?.items;
                const highlighted =
                    this.downshiftRef.current?.state.highlightedIndex;
                if (
                    this.props.shouldSelectItemOnTab &&
                    items &&
                    items.length > 0 &&
                    highlighted != null
                ) {
                    this.props.onZoneSelected(
                        items[highlighted],
                        KEY_TAB,
                        e.shiftKey
                    );
                } else {
                    if (e.shiftKey) {
                        this.props.onTabBack?.();
                    } else {
                        this.props.onTab?.();
                    }
                }
                break;
            }
            case KEY_ENTER: {
                const items = this.downshiftRef.current?.items;
                if (!items || items.length === 0) {
                    this.props.onEnter?.();
                }
                break;
            }
            case KEY_DELETE: {
                const { inputValue: iv } = this.state;
                if (!iv && this.props.onDelete) {
                    this.props.onDelete(DIRECTION_FORWARD);
                }
                break;
            }
            case KEY_LEFT: {
                const { inputValue: ivl } = this.state;
                if (!ivl && this.props.onMoveLeft) {
                    this.props.onMoveLeft();
                }
                break;
            }
            case KEY_RIGHT: {
                const { inputValue: ivr } = this.state;
                if (!ivr && this.props.onMoveRight) {
                    this.props.onMoveRight();
                }
                break;
            }
            default:
                break;
        }
    }

    handleInputKeyUp(e) {
        if (e.keyCode === KEY_BACKSPACE) {
            this.setState({ isDeleting: false });
        }
    }

    focus() {
        if (this.inputRef.current) {
            this.inputRef.current.focus();
            if (!this.props.initialChar) {
                this.inputRef.current.select();
            }
        }
    }

    renderZones(getItemProps, highlightedIndex) {
        if (this.state.matchedZones.length === 0) {
            return (
                <div className="downshift-menu-item" style={{ padding: '4%' }}>
                    No matching zones
                </div>
            );
        }
        return (
            <table
                className="ui compact table"
                style={{ width: '100%' }}
                border="0"
            >
                <tbody>
                    {this.state.matchedZones.map((item, index) => (
                        <tr
                            {...getItemProps({
                                item,
                                key: item.id,
                                className:
                                    FortDownshiftInput.getStandardClassForFortDownshiftItems(),
                                style: {
                                    backgroundColor:
                                        highlightedIndex === index
                                            ? 'rgba(0,0,0,0.05)'
                                            : 'white',
                                },
                            })}
                        >
                            {item.type === 'custom' ? (
                                <td
                                    style={{
                                        color: 'rgb(33, 150, 243)',
                                        paddingTop: '20px',
                                        paddingBottom: '20px',
                                    }}
                                >
                                    + ADD &quot;{item.name}&quot;
                                </td>
                            ) : (
                                <td>
                                    {item.displayName !== item.name
                                        ? `${item.displayName} (${item.name})`
                                        : item.name}
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    }

    render() {
        return (
            <Downshift
                ref={this.downshiftRef}
                itemToString={this.itemToString}
                stateReducer={this.stateReducer}
                defaultHighlightedIndex={0}
                initialInputValue={this.state.inputValue}
                inputValue={this.state.inputValue}
                initialIsOpen={this.state.initialIsOpen}
            >
                {({
                    getInputProps,
                    getItemProps,
                    isOpen,
                    highlightedIndex,
                }) => (
                    <div className="downshift-container">
                        <div className={this.props.inputClass}>
                            <input
                                data-cy={GRID_CELL_INPUT}
                                type="text"
                                {...getInputProps({
                                    ref: this.inputRef,
                                    onKeyDown: this.handleInputKeyDown,
                                    onKeyUp: this.handleInputKeyUp,
                                    className: 'downshift-input',
                                    placeholder: this.props.isLastPosition
                                        ? 'Zone or separator'
                                        : this.props.headerName || '',
                                })}
                            />
                        </div>
                        {isOpen && (
                            <div
                                className="downshift-content"
                                style={{
                                    ...FortDownshiftInput.getCssPositionPropInsideScrollAncestor(
                                        this.inputRef.current
                                    ),
                                    position: 'absolute',
                                    maxHeight: 'none',
                                    minWidth: '300px',
                                }}
                            >
                                <div
                                    className="downshift-menu"
                                    style={FortDownshiftInput.getCssPositionPropInsideScrollAncestor(
                                        this.inputRef.current
                                    )}
                                >
                                    {this.renderZones(
                                        getItemProps,
                                        highlightedIndex
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Downshift>
        );
    }
}

ZoneInput.propTypes = {
    onZoneSelected: PropTypes.func.isRequired,
    shouldSelectItemOnTab: PropTypes.bool.isRequired,
    inputClass: PropTypes.string,
    onTab: PropTypes.func,
    onTabBack: PropTypes.func,
    onEnter: PropTypes.func,
    initialChar: PropTypes.string,
    initialValue: PropTypes.string,
    onInputCleared: PropTypes.func,
    onSeparatorEntered: PropTypes.func,
    separatorCharacters: PropTypes.arrayOf(PropTypes.string),
    onDelete: PropTypes.func,
    onMoveLeft: PropTypes.func,
    onMoveRight: PropTypes.func,
    isLastPosition: PropTypes.bool,
    context: PropTypes.object.isRequired,
    field: PropTypes.string.isRequired,
    headerName: PropTypes.string,
};

export default ZoneInput;
